import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ROUTE_ITINERARY_RESULTS_BY_ROUTE } from '../../routes'
import { navigateTo } from '../../store/history/history.actions'
import { selectCurrentPolylines } from '../../store/itinerary/polylines.selectors'
import { selectCurrentRouteId } from '../../store/itinerary/routes.selectors'
import { useCallbackWithMapContext, useEffectWithMapContext } from '../map/MapContext'
import { getPolylinesAroundPointer } from '../utils/map'
import { useAction, withEverySelectorTruthy } from '../utils/react-redux'
import useErrorBoundaryCallback from '../utils/useErrorBoundaryCallback'
import Polyline from './Polyline'
import { areStepsFilled, selectArrivalStep, selectDepartureStep } from './steps/steps.selectors'
import { turnOffFullscreen } from '../../store/map/mapSlice'

const getFirstSectionId = features => {
  return features.map(feature => feature?.layer?.id ?? '')[0]
}

const getRouteId = sectionId => {
  const resultsRe = /_(\d+)_/.exec(sectionId)
  return resultsRe && resultsRe.length === 2 ? parseInt(resultsRe[1], 10) : ''
}

const Polylines = () => {
  const polylines = useSelector(selectCurrentPolylines)
  const from = useSelector(selectDepartureStep)
  const to = useSelector(selectArrivalStep)
  const currentRouteId = useSelector(selectCurrentRouteId)
  const execNavigateTo = useAction(navigateTo)
  const dispatch = useDispatch()
  const goToRoute = useErrorBoundaryCallback(
    routeId => {
      execNavigateTo({
        route: ROUTE_ITINERARY_RESULTS_BY_ROUTE,
        routeOptions: {
          avoidRefetchingPageData: true,
          avoidSendPageTag: true
        },
        selectedStoreState: {
          routeId
        }
      })
    },
    [execNavigateTo]
  )

  const handleClickOnPolyline = useCallbackWithMapContext(
    (map, e) => {
      const features = getPolylinesAroundPointer(map, e)
      const sectionId = getFirstSectionId(features)
      const routeId = getRouteId(sectionId)

      if (!e.handled && routeId && routeId !== currentRouteId) {
        e.handled = true
        dispatch(turnOffFullscreen())
        goToRoute(routeId)
      }
    },
    [getPolylinesAroundPointer, goToRoute, turnOffFullscreen, currentRouteId]
  )

  const enableEventsOnPolyline = useErrorBoundaryCallback(
    map => {
      map.on('click', handleClickOnPolyline)
    },
    [handleClickOnPolyline]
  )

  const disableEventsOnPolyline = useErrorBoundaryCallback(
    map => {
      map.off('click', handleClickOnPolyline)
    },
    [handleClickOnPolyline]
  )

  useEffectWithMapContext(
    map => {
      enableEventsOnPolyline(map)

      return () => {
        disableEventsOnPolyline(map)
      }
    },
    [enableEventsOnPolyline, disableEventsOnPolyline]
  )

  const [fromLabel, toLabel] = [from, to].map(({ label }) => label)

  return polylines.map(polyline => (
    <Polyline
      key={`from_${fromLabel}_to_${toLabel}_${polyline?.routeId}`}
      polyline={polyline}
      polylineCount={polylines.length}
    />
  ))
}

// webpack:BrowserOnly
export default withEverySelectorTruthy(areStepsFilled)(Polylines)
